import {
  CreateSavingsCategoryRequest,
  SavingsCategory,
  savingsCategoriesService,
  SetSavingsCategoryImagesRequest,
  UpdateSavingsCategoryRequest,
} from '@aslanpay/portal-common-lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SAVINGS_CATEGORIES_QUERY_KEY } from 'app/hooks/useFetchSavingsCategories';

interface CreateCategoryVariables {
  body: CreateSavingsCategoryRequest;
  companyId?: string;
}

interface UpdateCategoryVariables {
  id: string;
  body: UpdateSavingsCategoryRequest;
}

interface SetCategoryImagesVariables {
  id: string;
  body: SetSavingsCategoryImagesRequest;
}

/**
 * Bundles the savings-category write mutations (ASLAN_ADMIN only). Each refreshes the category
 * lists on success so global and per-company views stay in sync.
 */
const useSavingsCategoryMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [SAVINGS_CATEGORIES_QUERY_KEY] });

  const createCategory = useMutation<SavingsCategory, Error, CreateCategoryVariables>(
    ({ body, companyId }) => savingsCategoriesService.createSavingsCategory(body, companyId),
    { onSuccess: invalidate }
  );

  const updateCategory = useMutation<SavingsCategory, Error, UpdateCategoryVariables>(
    ({ id, body }) => savingsCategoriesService.updateSavingsCategory(id, body),
    { onSuccess: invalidate }
  );

  const deleteCategory = useMutation<unknown, Error, string>(
    (id) => savingsCategoriesService.deleteSavingsCategory(id),
    { onSuccess: invalidate }
  );

  const setCategoryImages = useMutation<SavingsCategory, Error, SetCategoryImagesVariables>(
    ({ id, body }) => savingsCategoriesService.setSavingsCategoryImages(id, body),
    { onSuccess: invalidate }
  );

  const cloneCategories = useMutation<unknown, Error, string>(
    (companyId) => savingsCategoriesService.cloneSavingsCategories(companyId),
    { onSuccess: invalidate }
  );

  const resetCategories = useMutation<unknown, Error, string>(
    (companyId) => savingsCategoriesService.resetSavingsCategories(companyId),
    { onSuccess: invalidate }
  );

  return {
    createCategory,
    updateCategory,
    deleteCategory,
    setCategoryImages,
    cloneCategories,
    resetCategories,
  };
};

export default useSavingsCategoryMutations;
